import { useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useQueryClient } from "@tanstack/react-query";

export type QuickCreateType = "action" | "task" | "idea" | "decision" | "milestone";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  type: QuickCreateType;
  meetingId: string;
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  onCreated?: (refCode: string) => void;
};

const TYPE_CONFIG: Record<
  QuickCreateType,
  { label: string; endpoint: string; refPrefix: string }
> = {
  action: { label: "Action", endpoint: "/actions", refPrefix: "ACT" },
  task: { label: "Task", endpoint: "/tasks", refPrefix: "TSK" },
  idea: { label: "Idea", endpoint: "/ideas", refPrefix: "IDT" },
  decision: { label: "Decision", endpoint: "/decisions", refPrefix: "DEC" },
  milestone: { label: "Milestone", endpoint: "/milestones", refPrefix: "MIL" },
};

export function QuickCreateModal({
  isOpen,
  onClose,
  type,
  meetingId,
  companyId,
  productId,
  projectId,
  onCreated,
}: Props) {
  const { accessToken, user } = useAuth();
  const qc = useQueryClient();
  const config = TYPE_CONFIG[type] ?? TYPE_CONFIG.task;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [dueDate, setDueDate] = useState("");
  const [ownerId, setOwnerId] = useState(user?.id ?? "");
  const [saving, setSaving] = useState(false);

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  function reset() {
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setDueDate("");
    setOwnerId(user?.id ?? "");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);

    try {
      let body: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim() || undefined,
        meetingId,
        companyId: companyId ?? undefined,
        productId: productId ?? undefined,
        projectId: projectId ?? undefined,
      };

      if (type === "action") {
        body = {
          ...body,
          priority,
          source: "MEETING",
          dueDate: dueDate || undefined,
          assignedToId: ownerId || undefined,
          createdById: user?.id,
        };
      } else if (type === "task") {
        body = {
          ...body,
          priority,
          dueDate: dueDate || undefined,
          assignedToId: ownerId || undefined,
          createdById: user?.id,
        };
      } else if (type === "idea") {
        body = {
          ...body,
          submittedById: user?.id,
        };
      } else if (type === "decision") {
        body = {
          ...body,
          decidedById: user?.id,
        };
      } else if (type === "milestone") {
        body = {
          ...body,
          dueDate: dueDate || undefined,
        };
      }

      const res = await apiFetch<Record<string, unknown>>(config.endpoint, {
        method: "POST",
        body,
        token: accessToken,
      });

      // Invalidate meeting queries to refresh tabs
      qc.invalidateQueries({ queryKey: ["meetings"] });

      const created = res[type] as { code?: string } | undefined;
      onCreated?.(created?.code ?? config.refPrefix);
      reset();
      onClose();
    } catch (err) {
      console.error(`[QuickCreate] failed to create ${type}:`, err);
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  const showPriority = type === "action" || type === "task";
  const showOwner = type === "action" || type === "task";
  const showDueDate = type === "action" || type === "task" || type === "milestone";

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-[9999] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-white shadow-2xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-base font-semibold text-foreground">
            Quick Create {config.label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="space-y-2">
            <Label htmlFor="qc-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="qc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${config.label} title`}
              autoFocus
              required
            />
          </div>

          {showOwner && (
            <div className="space-y-2">
              <Label htmlFor="qc-owner">Owner</Label>
              <select
                id="qc-owner"
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Unassigned</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {showPriority && (
              <div className="space-y-2">
                <Label htmlFor="qc-priority">Priority</Label>
                <Select
                  id="qc-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  {type === "task" && <option value="CRITICAL">Critical</option>}
                </Select>
              </div>
            )}

            {showDueDate && (
              <div className="space-y-2">
                <Label htmlFor="qc-due">Due Date</Label>
                <Input
                  id="qc-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="qc-desc">Description</Label>
            <textarea
              id="qc-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              placeholder="Description (optional)"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || saving}>
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />}
              Create {config.label}
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
